import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { PushPin as PinIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

// Uma linha arrastável do grupo fixado. Não há alça visível: os listeners do
// dnd-kit descem para o header do card via `dragHandleProps`, então o card
// fixado tem exatamente a mesma largura dos outros. Segurar o header arrasta,
// clicar abre a conversa — quem separa os dois é o `delay` dos sensores.
const SortablePinnedLead = ({ id, lead, renderLead }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const draggedRef = useRef(false);

  useEffect(() => {
    if (isDragging) draggedRef.current = true;
  }, [isDragging]);

  const swallowClickAfterDrag = (event) => {
    if (!draggedRef.current) return;
    draggedRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  };

  // Usuário precisa segurar no corpo do header para arrastar; clicar em qualquer
  // botão do header não arrasta. O wrapper percorre os listeners em vez de citar
  // `onPointerDown`: cada sensor tem o seu (onMouseDown/onTouchStart), e nomear
  // um na mão faz trocar de sensor desligar o arrasto em silêncio.
  const dragHandleProps = {
    'aria-roledescription': attributes['aria-roledescription'],
    ...Object.fromEntries(
      Object.entries(listeners).map(([name, handler]) => [
        name,
        (event) => {
          if (event.target.closest?.('button')) return;
          handler(event);
        },
      ]),
    ),
  };

  return (
    <Box
      ref={setNodeRef}
      onClickCapture={swallowClickAfterDrag}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 1 : 'auto',
        position: 'relative',
      }}
    >
      {renderLead(lead, dragHandleProps)}
    </Box>
  );
};

// Grupo das conversas fixadas, renderizado acima da lista normal com um cabeçalho
// próprio — a posição precisa ler como deliberada, não como bug de ordenação.
//
// `onReorder` recebe a nova ordem de uuids e devolve uma promise. Até o refetch
// do servidor chegar com essa ordem, a lista segue mostrando `dragOrder`; sem
// isso o card volta pro lugar antigo por alguns frames depois do drop.
const LeadsPinnedGroup = ({ leads, onReorder, renderLead }) => {
  const { t } = useTranslation();
  const [dragOrder, setDragOrder] = useState(null);

  const serverOrder = leads.map((lead) => lead.uuid).join(',');

  useEffect(() => {
    setDragOrder((current) =>
      current && current.join(',') === serverOrder ? null : current,
    );
  }, [serverOrder]);

  const ordered = useMemo(() => {
    if (!dragOrder) return leads;
    const byUuid = new Map(leads.map((lead) => [lead.uuid, lead]));
    const moved = dragOrder.map((uuid) => byUuid.get(uuid)).filter(Boolean);
    // Um lead fixado em outra aba/dispositivo não está em `dragOrder`; entra no fim.
    const missing = leads.filter((lead) => !dragOrder.includes(lead.uuid));
    return [...moved, ...missing];
  }, [leads, dragOrder]);

  // MouseSensor + TouchSensor, e não PointerSensor: no toque o browser dispara
  // `pointercancel` assim que decide que o gesto é rolagem, e como o header de
  // propósito não tem `touch-action: none` (senão não dava pra rolar a lista com
  // o dedo em cima dele), o PointerSensor era cancelado antes de ativar — o
  // arrasto simplesmente não acontecia no celular. O TouchSensor tem um
  // `setup()` que registra um `touchmove` não-passivo (o próprio dnd-kit marca
  // isso como necessário no iOS Safari) e só bloqueia a rolagem depois que o
  // arrasto ativa.
  //
  // Nos dois: 250ms parado = reorder; soltar antes é clique, que abre a
  // conversa; sair mais de 5px antes do prazo cancela e vira rolagem.
  const activationConstraint = { delay: 250, tolerance: 5 };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint }),
    useSensor(TouchSensor, { activationConstraint }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const uuids = ordered.map((lead) => lead.uuid);
    const next = arrayMove(uuids, uuids.indexOf(active.id), uuids.indexOf(over.id));

    setDragOrder(next);
    Promise.resolve(onReorder(next)).catch(() => setDragOrder(null));
  };

  if (ordered.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={ordered.map((lead) => lead.uuid)}
          strategy={verticalListSortingStrategy}
        >
          {ordered.map((lead) => (
            <SortablePinnedLead
              key={lead.uuid}
              id={lead.uuid}
              lead={lead}
              renderLead={renderLead}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default LeadsPinnedGroup;
